import { AUTO_AWAY_KIT, CUSTOM_KIT_ID, DEFAULT_AWAY_KIT, DEFAULT_HOME_KIT, KITS, type TeamSetup } from "@golazo/shared";
import { STARTER_KITS } from "./kits.js";
import { TOP_TIER, type Career } from "./model.js";

/**
 * Que equipaciones tienes. Se ganan en la carrera y se quedan para siempre:
 * una carrera nueva (o retirarse) no te quita nada. Se usan en todas partes,
 * tambien online, y viajan con la cuenta.
 *
 * La "A medida" (tus colores) tambien se gana: al llegar a Primera. Hasta
 * entonces, lo que vistes lo has sudado.
 */
export interface KitUnlocks {
  kits: string[];
  custom: boolean;
}

export interface UnlockStore {
  get(): KitUnlocks;
  has(kitId: string): boolean;
  /** Añade las que falten. Devuelve solo las nuevas. */
  add(kitIds: readonly string[]): string[];
  unlockCustom(): boolean;
  /** Lo que llega de otra copia (la de la cuenta): se suman, nunca se quitan. */
  merge(other: KitUnlocks): boolean;
  subscribe(fn: (unlocks: KitUnlocks) => void): () => void;
}

export const UNLOCKS_KEY = "fynta.kits";

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function sanitize(raw: unknown): KitUnlocks {
  const base: KitUnlocks = { kits: [...STARTER_KITS], custom: false };
  if (!raw || typeof raw !== "object") return base;
  const r = raw as Partial<KitUnlocks>;
  const kits = Array.isArray(r.kits) ? r.kits.filter((k): k is string => typeof k === "string" && k in KITS) : [];
  return { kits: [...new Set([...STARTER_KITS, ...kits])], custom: r.custom === true };
}

export function createUnlockStore(storage: StorageLike | null = safeLocalStorage()): UnlockStore {
  let current = sanitize(read());
  const listeners = new Set<(u: KitUnlocks) => void>();

  function read(): unknown {
    try {
      const raw = storage?.getItem(UNLOCKS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  const save = (): void => {
    try {
      storage?.setItem(UNLOCKS_KEY, JSON.stringify(current));
    } catch {
      // Sin almacenamiento: dura lo que la pestaña. La cuenta lo recupera.
    }
    for (const fn of listeners) fn(current);
  };

  return {
    get: () => current,
    has: (kitId) => current.kits.includes(kitId),
    add(kitIds) {
      const fresh = kitIds.filter((k) => k in KITS && !current.kits.includes(k));
      if (fresh.length === 0) return [];
      current = { ...current, kits: [...current.kits, ...fresh] };
      save();
      return fresh;
    },
    unlockCustom() {
      if (current.custom) return false;
      current = { ...current, custom: true };
      save();
      return true;
    },
    merge(other) {
      const incoming = sanitize(other);
      const kits = [...new Set([...current.kits, ...incoming.kits])];
      const custom = current.custom || incoming.custom;
      if (kits.length === current.kits.length && custom === current.custom) return false;
      current = { kits, custom };
      save();
      return true;
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

function safeLocalStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Un ajuste de equipaciones que solo usa lo que tienes. Lo que no tengas vuelve
 * al rojo (tu equipo) o al azul (el rival). "Automatica" para el rival se
 * respeta: es el rival, y el contraste manda.
 */
export function enforceUnlocks(setup: TeamSetup, unlocks: KitUnlocks): TeamSetup {
  const owned = (id: string): boolean => unlocks.kits.includes(id) || (id === CUSTOM_KIT_ID && unlocks.custom);
  return {
    ...setup,
    homeKitId: owned(setup.homeKitId) ? setup.homeKitId : DEFAULT_HOME_KIT,
    awayKitId:
      setup.awayKitId === AUTO_AWAY_KIT || owned(setup.awayKitId) ? setup.awayKitId : DEFAULT_AWAY_KIT,
  };
}

/**
 * Lo que la carrera ha ganado, al almacen de verdad: las camisetas de esta
 * carrera y la "A medida" al pisar Primera. Idempotente: se puede llamar
 * despues de cada paso. Devuelve solo lo que es nuevo, para celebrarlo.
 */
export function claimCareerUnlocks(store: UnlockStore, career: Career): { kits: string[]; custom: boolean } {
  const kits = store.add(career.earnedKits);
  const reachedTop = Math.max(career.peakTier, career.season.tier) >= TOP_TIER;
  const custom = reachedTop ? store.unlockCustom() : false;
  return { kits, custom };
}

let shared: UnlockStore | null = null;

/** La de este navegador, una sola para todo el juego. */
export function unlockStore(): UnlockStore {
  shared ??= createUnlockStore();
  return shared;
}
